/**
 * Plan-level actions, quiet to loud (19/10/2026: "minimalist, clean"): the
 * Notifications bell, enable/disable (a quiet text button while the plan is
 * on — a rare move; a real button when it is off), the kill switch
 * (compact, red, confirmed; Phase 20: it stops every campaign group) or its
 * Restore, and the live Generate button (drafts only — its own component
 * owns the confirm).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { Bell, Skull } from 'lucide-react';
import type { DayPlanPreview } from '@trafficpulse/shared';

import { Button, ConfirmDialog, Tooltip } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';

import type { DropGenPlanDetail } from '../../drop-generator.types';
import { useDropGeneratorMutations } from '../../useDropGeneratorMutations';
import { countryLabel } from './autosend/autosend-fact';
import { CreativeFillIndicator } from './CreativeFillIndicator';
import { GenerateButton } from './GenerateButton';

type PlanMove = 'enable' | 'disable' | 'kill' | 'unkill';
type Run = (action: PlanMove, message: string) => Promise<void>;

const KILL_TIP =
  'Emergency brake — blocks ALL generation and the auto-send of every campaign group of this country until Restored. Containers and config stay untouched.';

const KillOrRestore: FC<{ plan: DropGenPlanDetail; run: Run; busy: boolean; onKill: () => void }> = ({ plan, run, busy, onKill }) => {
  const canKill = useAuth().hasPermission('drop_generator.kill');
  if (plan.killSwitch)
    return (
      <Tooltip content="Turns the kill switch off — generation and auto-send are allowed again.">
        <Button variant="outline" size="sm" disabled={!canKill} loading={busy} onClick={() => run('unkill', 'Kill switch off')}>
          Restore
        </Button>
      </Tooltip>
    );
  return (
    <Tooltip content={KILL_TIP}>
      <Button variant="outline" size="sm" disabled={!canKill} onClick={onKill} className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive">
        <Skull className="mr-1 h-3.5 w-3.5" /> Kill
      </Button>
    </Tooltip>
  );
};

const PlanOnOff: FC<{ plan: DropGenPlanDetail; run: Run; busy: boolean }> = ({ plan, run, busy }) => {
  const blocked = plan.planIssues.length > 0 && !plan.enabled;
  const tip = blocked
    ? "Can't enable — set the country's timezone first (click the Window)."
    : plan.enabled
      ? 'Routine off switch — the generator skips this country until you enable it again.'
      : "Turns the plan on so this country's days can be laid out.";
  return (
    <Tooltip content={tip}>
      <span>
        <Button
          variant={plan.enabled ? 'ghost' : 'outline'}
          size="sm"
          loading={busy}
          disabled={blocked}
          className={plan.enabled ? 'text-muted-foreground' : undefined}
          onClick={() => (plan.enabled ? run('disable', 'Plan disabled') : run('enable', 'Plan enabled'))}
        >
          {plan.enabled ? 'Disable plan' : 'Enable plan'}
        </Button>
      </span>
    </Tooltip>
  );
};

const NotificationsBell: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Tooltip content="Notifications — the Live feed's decisions and the daily summaries, on Slack or Telegram">
    <Button variant="ghost" size="sm" onClick={onClick} aria-label="Notifications" className="w-9 px-0 text-muted-foreground">
      <Bell className="h-4 w-4" />
    </Button>
  </Tooltip>
);

export const HeaderActions: FC<{
  plan: DropGenPlanDetail;
  preview: DayPlanPreview | undefined;
  /** Phase 32 — opens the country's alert channels (Telegram, Slack). */
  onNotifications?: () => void;
}> = ({ plan, preview, onNotifications }) => {
  const { planAction } = useDropGeneratorMutations();
  const { success, error } = useToast();
  const [confirmingKill, setConfirmingKill] = useState(false);
  const run: Run = async (action, message) => {
    try {
      await planAction.mutateAsync({ countryKey: plan.countryKey, action });
      success(message);
      setConfirmingKill(false);
    } catch (err) {
      error('Action failed', getApiErrorMessage(err));
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <CreativeFillIndicator countryKey={plan.countryKey} />
      {onNotifications ? <NotificationsBell onClick={onNotifications} /> : null}
      <PlanOnOff plan={plan} run={run} busy={planAction.isPending} />
      <KillOrRestore plan={plan} run={run} busy={planAction.isPending} onKill={() => setConfirmingKill(true)} />
      <GenerateButton plan={plan} preview={preview} />
      <ConfirmDialog
        open={confirmingKill}
        title="Flip the kill switch?"
        description={killWords(plan.countryKey)}
        confirmLabel="Kill — every group"
        destructive
        busy={planAction.isPending}
        onConfirm={() => run('kill', 'Kill switch on — nothing generates or sends')}
        onCancel={() => setConfirmingKill(false)}
      />
    </div>
  );
};

const killWords = (countryKey: string) =>
  `Nothing will generate or auto-send in any campaign group of ${countryLabel(countryKey)} until it is restored — the very next send stops. Containers and their config stay untouched.`;
